import React from 'react';
import { styled, css } from 'styled-components';
import LoadingSpinner from '@common/LoadingSpinner/LoadingSpinner';
import { FontSize } from '@themes/Fonts';
import Styles from '@themes/Styles';

export const BaseButton = styled.button.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'fullWidth',
      'overrideBackgroundColor',
      'overrideTextColor',
      'disableHover',
      'variant',
      'isDisabled',
      'size',
    ].includes(prop),
})<{
  fullWidth?: boolean;
  overrideBackgroundColor?: string;
  overrideTextColor?: string;
  disableHover?: boolean;
  variant?: ButtonVariant;
  isDisabled?: boolean;
  size?: ButtonSize;
}>`
  padding: unset;
  text-decoration: none;
  position: relative;
  border-radius: ${(props) => (props.size === 'small' ? '5px' : '7px')};
  width: ${(props) => (props.fullWidth ? `100%` : `fit-content`)};
  cursor: ${(props) => (props.isDisabled ? `not-allowed` : `pointer`)};
  opacity: ${(props) => (props.isDisabled ? `0.5` : `1`)};

  background-color: ${(props) => {
    switch (props.variant) {
      case 'inverseColor': {
        return props.overrideBackgroundColor ?? props.theme.textColorPrimary;
      }
      case 'solid': {
        return props.overrideBackgroundColor ?? props.theme.backgroundPrimary;
      }
      case 'secondary': {
        return props.overrideBackgroundColor ?? props.theme.backgroundSpecial;
      }
      case 'ghost': {
        return `unset`;
      }
      case 'active': {
        return props.theme.hoverPrimary;
      }
      default: {
        return `unset`;
      }
    }
  }};

  border: ${(props) =>
    props.variant === 'outline' ? `1px solid ${props.overrideTextColor ?? props.theme.borderPrimary}` : 'none'};

  ${(props) =>
    !props.disableHover &&
    css`
      &:hover {
        background-color: ${(props) => props.theme.hoverPrimary};
      }
    `};
`;

const ButtonContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['overrideTextColor', 'size', 'variant', 'shape', 'hideContent'].includes(prop),
})<{
  overrideTextColor?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  shape?: ButtonShape;
  hideContent?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  color: ${(props) => {
    if (props.overrideTextColor) {
      return props.overrideTextColor;
    } else if (props.variant === 'inverseColor') {
      return props.theme.backgroundPrimary;
    } else {
      return props.theme.textColorPrimary;
    }
  }};

  ${(props) =>
    props.hideContent &&
    css`
      visibility: hidden;
    `}

  font-size: ${(props) => {
    if (props.size === 'small') {
      return FontSize.small;
    } else if (props.size === 'large') {
      return FontSize.default;
    } else {
      return FontSize.normal;
    }
  }};

  padding: ${(props) => {
    if (props.size === 'small') {
      return props.shape === 'square' ? '6px' : '6px 8px';
    } else if (props.size === 'large') {
      return props.shape === 'square' ? '8px' : '8px 15px';
    } else {
      return props.shape === 'square' ? '6px' : '6px 15px';
    }
  }};
`;

const LoadingSpinnerContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type ButtonVariant = 'solid' | 'secondary' | 'outline' | 'ghost' | 'active' | 'inverseColor';
export type ButtonSize = 'small' | 'medium' | 'large';
type ButtonShape = 'normal' | 'square';

export interface LifeAtBaseButtonProps {
  readonly children: React.ReactNode[] | React.ReactNode;
  readonly onClick?: () => void;
  readonly href?: string;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly shape?: ButtonShape;
  readonly disableHover?: boolean;
  readonly isDisabled?: boolean;
  readonly fullWidth?: boolean;
  readonly overrideBackgroundColor?: string;
  readonly overrideTextColor?: string;
  readonly isLoading?: boolean;
  readonly className?: string;
  readonly openExternally?: boolean;
  readonly shouldPreventDefault?: boolean;
}
export const LifeAtButtonV2 = React.forwardRef(function LifeAtBaseButton(
  props: LifeAtBaseButtonProps,
  forwardRef: React.ForwardedRef<HTMLAnchorElement & HTMLButtonElement>,
) {
  return (
    <BaseButton
      as={props.href ? 'a' : 'button'}
      ref={forwardRef}
      href={props.href}
      target={props.openExternally ? '_blank' : undefined}
      rel={props.openExternally ? 'noopener noreferrer' : undefined}
      onClick={(e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (props.shouldPreventDefault) {
          e.preventDefault();
        }

        !props.isDisabled && props.onClick?.();
      }}
      className={props.className}
      fullWidth={props.fullWidth}
      size={props.size}
      overrideBackgroundColor={props.overrideBackgroundColor}
      overrideTextColor={props.overrideTextColor}
      disableHover={props.disableHover}
      variant={!props.variant && props.overrideBackgroundColor ? 'solid' : props.variant}
      isDisabled={props.isDisabled}
    >
      {props.isLoading && (
        <LoadingSpinnerContainer>
          <LoadingSpinner size={20} color={props.overrideTextColor ?? Styles.lifeatSpecialRed} />
        </LoadingSpinnerContainer>
      )}
      <ButtonContent
        className={props.className}
        overrideTextColor={props.overrideTextColor}
        size={props.size}
        variant={props.variant}
        shape={props.shape}
        hideContent={props.isLoading}
      >
        {props.children}
      </ButtonContent>
    </BaseButton>
  );
});
